import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Intl } from '@erecruit/core';
import {
  Icon,
  Button,
  Text,
  H3,
  Flex,
  FlexItem,
  Badge,
  Tag,
  styled,
  Divider,
  divider,
  Dropdown,
  List,
  Tooltip,
  DropdownPopup,
} from '@erecruit/ui-kit';
import { connect } from 'react-redux';
import { NotificationContext, Notification } from '@erecruit/modules';
import { notificationActions, notificationSelectors } from '../state/reducer';
import { NotificationContent, LinkDefinition } from './BasicNotification';
import { settingsMap, SettingsKey } from '../../Settings/settingsMap';
import { AppNotification } from '../types';
import {
  getNotificationLink,
  notificationTypesLocalizations,
  PAGE_SIZE,
} from '../helpers';

const { FormattedMessage, injectIntl } = Intl;

const NOTIFICATION_ROUTE = '/notifications';

interface NotificationPopupProps extends RouteComponentProps {
  markAllAsRead: () => void;
  markAsRead: (notificationId: string) => void;
  notifications: Notification[];
  fullUnreadLoaded: boolean;
  isLoading: boolean;
  getUnreadNotifications: typeof notificationActions['getUnreadNotifications'];
  unreadCount: number;
}

interface NotificationListItemProps extends Divider {
  notification: AppNotification;
  className?: 'string';
  onMarkAsRead?: (notificationId: string) => void;
}

export const NotificationListItem = injectIntl(
  styled(
    ({
      intl,
      notification: { link, body, createdOn, data, type, id, read },
      className,
      onMarkAsRead,
    }: NotificationListItemProps & Intl.InjectedIntlProps) => {
      const notificationLink: LinkDefinition | undefined = useMemo(() => {
        if (link) return link;

        const parsedContexts = JSON.parse(
          data.context,
        ) as NotificationContext[];
        const generatedLink = getNotificationLink(parsedContexts[0]);

        if (generatedLink) {
          return {
            to: generatedLink,
            text: intl.formatMessage({ id: messageIds.vms.notifications.link }),
          };
        }
      }, [intl, link, data]);

      return (
        <Flex padding={[0, 3]} className={className}>
          <NotificationContent
            id={id}
            link={notificationLink}
            title={intl.formatMessage({
              id: notificationTypesLocalizations[type],
            })}
            type={type}
            body={body}
            time={createdOn}
            read={read}
            onMarkAsRead={onMarkAsRead}
          />
        </Flex>
      );
    },
  )<{ notification: Notification }>(({ theme, notification }) => ({
    ':not(:first-of-type)': divider({ theme, divider: 'top' }),
    backgroundColor: notification.read
      ? theme.newColors.bgPageSecondary
      : theme.newColors.bgStandard,
  })),
);

export const NotificationPopup = withRouter(
  ({
    markAllAsRead,
    markAsRead,
    notifications,
    history,
    fullUnreadLoaded,
    isLoading,
    getUnreadNotifications,
    unreadCount,
    ...props
  }: NotificationPopupProps) => {
    const [pagination, setPagination] = useState({
      offset: 0,
      limit: PAGE_SIZE,
    });

    const loadUnread = useCallback(
      e => {
        const newPagination = {
          offset: pagination.offset + PAGE_SIZE,
          limit: PAGE_SIZE,
        };
        setPagination(newPagination);
        e.stopPropagation();
      },
      [pagination],
    );

    useEffect(() => {
      getUnreadNotifications(pagination);
    }, [pagination, getUnreadNotifications]);

    const loadMoreUnread =
      !isLoading && !fullUnreadLoaded ? (
        <Flex padding={4} align="center" justify="center">
          <Button onClick={loadUnread}>
            <FormattedMessage id={messageIds.vms.notificationPopup.loadMore} />
          </Button>
        </Flex>
      ) : null;

    return (
      <DropdownPopup {...props}>
        <Flex
          justify="space-between"
          align="center"
          padding={[2, 6]}
          gutter={3}
        >
          <FlexItem>
            <H3>
              <FormattedMessage id={messageIds.vms.notificationPopup.title} />
            </H3>
          </FlexItem>
          {!!notifications.length && (
            <FlexItem flex={0}>
              <Tooltip
                overlay={
                  <Intl.FormattedMessage
                    id={messageIds.vms.notificationPopup.markAllAsReadCaption}
                  />
                }
                placement="top"
              >
                <Icon type="hide" onClick={markAllAsRead} />
              </Tooltip>
            </FlexItem>
          )}
          <FlexItem flex={0}>
            <Tooltip
              overlay={
                <Intl.FormattedMessage
                  id={messageIds.vms.notificationPopup.historyCaption}
                />
              }
              placement="top"
            >
              <Icon
                type="recent"
                onClick={() =>
                  history.push(NOTIFICATION_ROUTE, { modal: true })
                }
              />
            </Tooltip>
          </FlexItem>
          <FlexItem flex={0}>
            <Tooltip
              overlay={
                <Intl.FormattedMessage
                  id={messageIds.vms.notificationPopup.settingsCaption}
                />
              }
              placement="top"
            >
              <Icon
                type="settings"
                onClick={() =>
                  history.push(settingsMap[SettingsKey.Notifications].route)
                }
              />
            </Tooltip>
          </FlexItem>
        </Flex>
        {!notifications.length && (
          <Flex padding={[2, 6]} divider="top" justify="center" align="center">
            <Text color="bodySecondary">
              <FormattedMessage
                id={messageIds.vms.notificationPopup.emptyMessage}
              />
            </Text>
          </Flex>
        )}
        <List
          loading={isLoading}
          loadMore={loadMoreUnread}
          dataSource={notifications}
          renderItem={item => (
            <NotificationListItem
              key={item.id}
              notification={item}
              onMarkAsRead={markAsRead}
            />
          )}
        />
        <Flex divider="top" padding={5}>
          <Text color="bodySecondary">
            Showing {notifications.length} out of {unreadCount} unread
            notifications
          </Text>
        </Flex>
      </DropdownPopup>
    );
  },
);

const StyledTag = styled(Tag)(({ theme: { newColors } }) => ({
  '& :hover': {
    backgroundColor: newColors.bgRowHover,
    borderRadius: 4,
  },
}));

export const StyledNotificationStand = styled(
  ({
    unreadCount,
    markAllAsRead,
    markAsRead,
    className,
    notifications,
    getUnreadNotifications,
    isLoadingUnread,
    fullUnreadLoaded,
    ...props
  }) => (
    <Dropdown
      overlay={
        <NotificationPopup
          markAllAsRead={markAllAsRead}
          markAsRead={markAsRead}
          notifications={notifications}
          isLoading={isLoadingUnread}
          getUnreadNotifications={getUnreadNotifications}
          fullUnreadLoaded={fullUnreadLoaded}
          unreadCount={unreadCount}
        />
      }
      overlayClassName={className}
      {...props}
    >
      {(visible: boolean) => (
        <Badge count={unreadCount} offset={[-3, 3]}>
          <StyledTag
            color={visible ? 'bgRowHover' : 'bgPageSecondary'}
            collapse
          >
            <Icon type="notification" />
          </StyledTag>
        </Badge>
      )}
    </Dropdown>
  ),
)({
  width: 352,
  zIndex: 10000,
  '& > div > div:nth-of-type(2)': {
    maxHeight: '30vh',
    overflow: 'auto',
  },
});

export const NotificationStand = connect(
  state => ({
    unreadCount: notificationSelectors.getUnreadCount(state),
    notifications: notificationSelectors.getUnread(state),
    isLoadingUnread: notificationSelectors.getIsLoadingUnread(state),
    fullUnreadLoaded: notificationSelectors.getFullUnreadLoaded(state),
  }),
  {
    markAllAsRead: notificationActions.markAllAsRead,
    markAsRead: notificationActions.markAsRead,
    getUnreadNotifications: notificationActions.getUnreadNotifications,
  },
)(StyledNotificationStand);
